import { Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { EntityUpdateService } from './entity-update.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
export interface TierOption {
  VALUE: string | number;
  LABEL: string;
  [key: string]: unknown;
}

export interface ColumnNameOption {
  COLUMN_NAME: string;
  TABLE_NAME?: string;
  DATA_TYPE?: string;
  DATA_LENGTH?: number;
  NULLABLE?: string;
  [key: string]: unknown;
}

export interface TransTypeOption {
  VALUE: string | number;
  LABEL: string;
  isChecked?: boolean;
  [key: string]: unknown;
}

@Component({
    selector: 'gtw-entity-update',
    templateUrl: './entity-update.component.html',
    styleUrls: ['./entity-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityUpdateComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  modalTitle = 'Entity Update';
  minimise = false;
  isSaveClicked = false;
  /** Loader shown only on Save button */
  saveLoading = false;
  /** Tracks which delete type is in progress (null = none) */
  deletingType: string | null = null;
  /** Loader shown only on Cleanup Change Log button */
  cleanupLoading = false;
  showFormLoader = true;

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  tier: TierOption | null = null;
  tierDisplay = '** SELECT TIER VALUE **';
  columnName: ColumnNameOption | null = null;
  columnNameDisplay = '';
  columnValue = '';
  tier_data: TierOption[] = [];
  column_name_data: ColumnNameOption[] = [];
  tableName = '';
  validationInfo = '';
  tierSelected = false;
  columnNameSelected = false;

  TRANS_TYPE_DATA: {
    label: string;
    values: TransTypeOption[];
    selected: TransTypeOption[];
    param_name?: string;
    label_name?: string;
    value_name?: string;
  } = {
    label: 'Transactions',
    label_name: 'LABEL',
    param_name: 'Transactions',
    selected: [],
    value_name: 'VALUE',
    values: [],
  };
  transactionData: any[] = [];
  /** True after trans type API has been received (so we can show "No Transactions Found" only when loaded and empty) */
  transDataLoaded = false;
  screen_key = '';
  GT_SYNC_DATA: { VALUE: string | number; LABEL: string }[] = [];
  cng_type: { VALUE: string | number; LABEL: string } | null = null;
  cng_typeDisplay = '';

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = { title: '', message: '' };
  private confirmMode: 'cleanup' | 'delete' | null = null;
  private pendingDeleteType: string | null = null;

  private isInitialized = false;
  private isLoading = false;

  constructor(
    private entityUpdateService: EntityUpdateService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const u = typeof v === 'string' ? safeJsonInput<any>(v, null, 'entity-update.parse1') : v;
      if (u && Object.keys(u).length > 0) {
        this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, ...u };
        this.tryInit();
      }
    } catch (e) {
      console.log('Base URLs input not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const h = typeof v === 'string' ? safeJsonInput<any>(v, null, 'entity-update.parse2') : v;
      if (h && Object.keys(h).length > 0) {
        this.globalService = h.globalParams || {};
        this.USER_SETTINGS = h.userSettingsData || {};
        this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, ...(h.generalConfigData || {}) };
        if (!this.screen_key && this.globalService?.screen_key) {
          this.screen_key = String(this.globalService.screen_key);
        }
        this.tryInit();
      }
    } catch (e) {
      console.log('Header data input not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(v: string) {
    try {
      const d = typeof v === 'string' ? safeJsonInput<any>(v, null, 'entity-update.parse3') : v;
      if (d && Object.keys(d).length > 0) {
        this.rowData = d;
        this.tryInit();
      }
    } catch (e) {
      console.log('Row data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      const p = typeof v === 'string' ? safeJsonInput<any>(v, null, 'entity-update.parse4') : v;
      if (p && Object.keys(p).length > 0) {
        this.filterParams = p;
        // this.screen_key = p.screen_key ?? this.globalService?.screen_key ?? '';
        this.tryInit();
      }
    } catch (e) {
      console.log('Filter params input not yet stable.');
    }
  }

  @Input('screen-key')
  set _screen_key(v: string) {
    this.screen_key = v;
    this.tryInit();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (
      !this.globalService ||
      !this.GENERAL_CONFIG?.base_url ||
      !this.rowData ||
      Object.keys(this.rowData).length === 0 ||
      this.screen_key === ''
      || !this.filterParams || Object.keys(this.filterParams).length === 0
    ) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.showFormLoader = true;
      this.isLoading = true;
      this.cdr.detectChanges();

      const combinationKey = this.rowData.COMBINATION_KEY;

      forkJoin({
        tier: this.entityUpdateService.getTierData(
          this.baseUrl,
          this.globalService,
          this.filterParams,
          this.ssoId
        ),
        trans: this.entityUpdateService.getTransType(
          this.baseUrl,
          combinationKey,
          this.globalService,
          this.filterParams,
          this.ssoId
        ),
        sync: this.entityUpdateService.getSyncDropdownData(
          this.baseUrl,
          this.globalService,
          this.filterParams,
          this.ssoId
        ),
      })
        .pipe(
          catchError((err) => {
            console.error(err);
            this.gtwSnackbarService.openSnackBar(err?.message || 'Error loading data', 'gtw-snackbar--red');
            return of({ tier: {}, trans: {}, sync: {} });
          }), takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((res) => {
          this.isLoading = false;
          this.showFormLoader = false;

          if (res.tier?.jsonObject) {
            this.tier_data = res.tier.jsonObject;
          }
          if (res.trans !== undefined) {
            this.transDataLoaded = true;
            const transValues = Array.isArray(res.trans?.jsonObject)
              ? res.trans.jsonObject
              : Array.isArray(res.trans?.data)
                ? res.trans.data
                : Array.isArray(res.trans) ? res.trans : [];
            this.TRANS_TYPE_DATA = {
              ...this.TRANS_TYPE_DATA,
              label: 'Transactions',
              label_name: 'LABEL',
              param_name: 'Transactions',
              selected: this.TRANS_TYPE_DATA?.selected ?? [],
              value_name: 'VALUE',
              values: transValues,
            };
          }
          if (res.sync?.jsonObject) {
            this.GT_SYNC_DATA = res.sync.jsonObject;
          }
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  onTierChange(option: TierOption | null): void {
    this.tier = option;
    this.tierDisplay = option ? option.LABEL : '** SELECT TIER VALUE **';
    this.tierSelected = !!option;
    this.columnNameSelected = false;
    this.columnName = null;
    this.columnNameDisplay = '';
    this.columnValue = '';
    this.validationInfo = '';
    this.column_name_data = [];
    this.tableName = '';

    if (option?.VALUE) {
      this.entityUpdateService
        .getColumnNames(
          this.baseUrl,
          String(option.VALUE),
          this.globalService,
          this.filterParams,
          this.ssoId
        ).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            if (res?.jsonObject?.length) {
              this.column_name_data = res.jsonObject;
              this.tableName = res.jsonObject[0]?.TABLE_NAME ?? '';
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error(err);
            this.cdr.detectChanges();
          },
        });
    }
    this.cdr.detectChanges();
  }

  onColumnNameChange(option: ColumnNameOption | null): void {
    this.columnName = option;
    this.columnNameDisplay = option ? option.COLUMN_NAME : '';
    this.columnNameSelected = !!option;
    if (option) {
      this.validationInfo = `This input field is ${option.DATA_TYPE ?? 'text'}`;
      if (option.DATA_TYPE !== 'DATE') {
        this.validationInfo += `, and the length is ${option.DATA_LENGTH ?? 'N/A'}`;
      }
      if (option.DATA_TYPE === 'DATE') {
        this.validationInfo += `, and the date format should be 'MM/DD/YYYY'`;
      }
      if (option.NULLABLE === 'N') {
        this.validationInfo += ', and should not be null';
      }
    } else {
      this.validationInfo = '';
    }
    this.cdr.detectChanges();
  }

  onTransTypeFilterChange(): void {
    this.buildTransactionData();
    this.cdr.detectChanges();
  }

  private buildTransactionData(): void {
    this.transactionData = (this.TRANS_TYPE_DATA?.selected ?? []).map((data) => ({
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      TRANS_TYPE_KEY: data.VALUE,
    }));
  }

  onCngTypeChange(option: { VALUE: string | number; LABEL: string } | null): void {
    this.cng_type = option;
    this.cng_typeDisplay = option ? option.LABEL : '';
    this.cdr.detectChanges();
  }

  /**
   * Returns COLUMN_VALUE in the type the backend expects (number for numeric/LE_KEY columns, string otherwise).
   * Matches legacy JS behavior so 0jag4h does not throw when column is le_key and input is numeric.
   */
  private getColumnValueForSave(): string | number | null {
    const raw = this.columnValue;
    const dataType = (this.columnName?.DATA_TYPE ?? '').toUpperCase();
    const columnName = (this.columnName?.COLUMN_NAME ?? '').toUpperCase();
    const isNumericColumn =
      dataType === 'NUMBER' ||
      dataType === 'NUMERIC' ||
      dataType === 'INT' ||
      dataType === 'INTEGER' ||
      columnName === 'LE_KEY';

    if (!isNumericColumn) {
      return raw;
    }
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const num = Number(raw);
    return Number.isNaN(num) ? raw : num;
  }

  private checkValidations(): boolean {
    if (!this.columnName) return false;
    if (this.columnName.DATA_TYPE !== 'DATE') {
      if (this.columnValue.length > (this.columnName.DATA_LENGTH ?? 0)) {
        this.gtwSnackbarService.openSnackBar(
          `Length of Column Value is greater than ${this.columnName.DATA_LENGTH}`,
          'gtw-snackbar--red'
        );
        return false;
      }
    } else if (this.columnName.DATA_TYPE === 'DATE') {
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
      if (!dateRegex.test(this.columnValue)) {
        this.gtwSnackbarService.openSnackBar('Date should be in correct format of MM/dd/yyyy', 'gtw-snackbar--red');
        return false;
      }
    }
    if (this.columnName.NULLABLE === 'N' && this.columnValue === '') {
      this.gtwSnackbarService.openSnackBar('Column Value cannot be null', 'gtw-snackbar--red');
      return false;
    }
    return true;
  }

  save(): void {
    if (this.saveLoading || !this.columnNameSelected || !this.columnName) return;
    if (!this.checkValidations()) return;

    this.isSaveClicked = true;
    this.saveLoading = true;
    this.cdr.detectChanges();

    const formData = {
      ...this.rowData,
      COLUMN_NAME: this.columnName.COLUMN_NAME,
      COLUMN_VALUE: this.getColumnValueForSave(),
    };
    const clob_data = [formData];

    this.entityUpdateService
      .saveEntity(
        this.baseUrl,
        clob_data,
        String(this.tier?.VALUE ?? ''),
        this.globalService,
        this.filterParams,
        this.ssoId,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.isSaveClicked = false;
          this.saveLoading = false;
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Update Success!', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSaveClicked = false;
          this.saveLoading = false;
          this.gtwSnackbarService.openSnackBar(err?.message || 'Error saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  deleteCodeCombi(delType: string): void {
    this.confirmMode = 'delete';
    this.pendingDeleteType = delType;
    this.confirmModalConfig = {
      title: delType === 'DELETE_TRANS' ? 'Delete Transaction(s)' : 'Delete Code Combination',
      message:
        delType === 'DELETE_TRANS'
          ? 'Are you sure you want to delete this transaction?'
          : 'Are you sure you want to delete this Code Combination?',
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...',
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  cleanupChangelog(): void {
    if (!this.cng_type?.VALUE || !this.rowData?.LE_KEY) {
      this.gtwSnackbarService.openSnackBar('Please select a change type.', 'gtw-snackbar--yellow');
      return;
    }
    this.confirmMode = 'cleanup';
    this.confirmModalConfig = {
      title: 'Cleanup Change Log',
      message: 'Are you sure you want to cleanup change log?',
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...',
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    const mode = this.confirmMode;
    this.showConfirmDialog = false;
    this.confirmMode = null;
    this.cdr.detectChanges();

    if (mode === 'cleanup') {
      this.executeCleanupChangelog();
    } else if (mode === 'delete') {
      this.executeDeleteCodeCombi();
    }
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.confirmMode = null;
    this.pendingDeleteType = null;
    this.cdr.detectChanges();
  }

  private executeCleanupChangelog(): void {
    this.cleanupLoading = true;
    this.cdr.detectChanges();

    this.entityUpdateService
      .entityChangeCleanup(
        this.baseUrl,
        String(this.cng_type!.VALUE),
        String(this.rowData.LE_KEY),
        this.globalService,
        this.filterParams,
        this.ssoId,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.cleanupLoading = false;
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Update Success!', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.cleanupLoading = false;
          this.gtwSnackbarService.openSnackBar(err?.message || 'Error cleaning up.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private executeDeleteCodeCombi(): void {
    const delType = this.pendingDeleteType;
    this.pendingDeleteType = null;

    if (!delType) return;

    this.deletingType = delType;
    this.cdr.detectChanges();

    if (delType === 'DELETE_TRANS') {
      this.buildTransactionData();
    } else {
      this.transactionData = [{ COMBINATION_KEY: this.rowData.COMBINATION_KEY }];
    }

    this.entityUpdateService
      .deleteCodeCombi(
        this.baseUrl,
        this.transactionData,
        delType,
        this.globalService,
        this.filterParams,
        this.ssoId,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.deletingType = null;
          if (result?.errorMessage && result?.callSuccess === '0') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Delete Successful!', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.deletingType = null;
          this.gtwSnackbarService.openSnackBar(err?.message || 'Error deleting.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
